import React, {useEffect, useState} from 'react';
import {Typography} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro/DataGridPro';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import './ErrorGridModal.scss';
import {
  useExportErrorList,
  useGetErrorList,
  useGetNativeErrorList,
} from '../../../../../hooks/productions/useProductions';
import {GetErrorListRequestPayload} from '../../../../../api/types/production.type';
import {ToastType} from '../../../../../shared/constants';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {
  ErrorGridType,
  placeholderErrorColumns,
  productionErrorColumns,
  transformApiDataToRows,
} from './utils';
import {FooterWrapper} from './CustomFooter';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {ModuleType} from '../../CreateNewProductionUtils';
import {t} from 'i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ReplaceSeedPdfFlyout from './ReplaceSeedPdfFlyout';
import {GridSortModel} from '@mui/x-data-grid-pro';

interface ErrorGridModalProps {
  productionId: number;
  securityGroupId: number;
  open: boolean;
  title?: string;
  tooltip?: string;
  gridType: ErrorGridType;
  onClose: () => void;
}

const ErrorGridModal: React.FC<ErrorGridModalProps> = ({
  productionId,
  securityGroupId,
  open,
  title,
  gridType,
  tooltip,
  onClose,
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [isReplaceSeedPdfFlyoutOpen, setIsReplaceSeedPdfFlyoutOpen] =
    useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<number>(0);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'DocID', sort: 'asc'},
  ]);

  const handleReplaceSeedPdfClick = (documentId: number) => {
    setSelectedDocumentId(documentId);
    setIsReplaceSeedPdfFlyoutOpen(true);
  };

  const handleReplaceSeedPdfFlyoutClose = () => {
    setIsReplaceSeedPdfFlyoutOpen(false);
    setSelectedDocumentId(0);
  };

  const columns = (
    gridType === ErrorGridType.ProductionErrors
      ? productionErrorColumns
      : placeholderErrorColumns
  ).map((col) => {
    if (col.field === 'replaceSeedPdf') {
      return {
        ...col,
        headerName: t(
          'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.title'
        ),
        renderCell: (params: any) => {
          const documentId = params.row.hiddenDocumentId;

          if (params.row.isEmpty) {
            return (
              <div
                className="auto-cell"
                data-testid={`grid-cell-replaceSeedPdf-${params.id}`}
              >
                {params.value}
              </div>
            );
          }

          return (
            <div
              className={`auto-cell ${
                params.value === 'Yes' ? 'has-replace-button' : ''
              }`}
              data-testid={`grid-cell-replaceSeedPdf-${params.id}`}
            >
              {params.value === 'Yes' ? (
                <ArrButton
                  variant="secondary"
                  size="xs"
                  onClick={() => handleReplaceSeedPdfClick(documentId)}
                  dataTestId={`replace-seed-pdf-btn-${documentId}`}
                  className="replace-seed-pdf-button"
                >
                  {t(
                    'productionsAndExports.createNewProduction.generate.modals.replaceSeedPdf.title'
                  )}
                </ArrButton>
              ) : (
                <span
                  data-testid={`error-grid-modal-replaceSeedPdf-${params.id}`}
                >
                  {params.value}
                </span>
              )}
            </div>
          );
        },
      };
    }
    return {
      ...col,
      renderCell: (params: any) => (
        <div
          className="auto-cell"
          data-testid={`grid-cell-${col.field}-${params.id}`}
        >
          {params.value}
        </div>
      ),
    };
  });

  useEffect(() => {
    if (open) {
      modalRef.current?.focus();
    }
  }, [open]);
  const sortingColumn = sortModel[0]?.field || 'DocID';
  const direction = sortModel[0]?.sort || 'asc';

  const requestPayload: GetErrorListRequestPayload = {
    productionId,
    productionSecurityGroupId: securityGroupId,
    pageNumber: currentPageNumber + 1,
    displayLength: pageSize,
    sortingColumn,
    direction,
    qcTypeLabel: '',
  };

  const isProduction = ErrorGridType.ProductionErrors === gridType;

  const {data: productionErrors} = useGetErrorList(
    requestPayload,
    isProduction && open
  );

  const {data: nativeErrors} = useGetNativeErrorList(
    requestPayload,
    !isProduction && open
  );

  const errorListData = isProduction ? productionErrors : nativeErrors;
  useEffect(() => {
    setPageCount(
      errorListData?.totalRecords
        ? Math.ceil(errorListData.totalRecords / pageSize)
        : 0
    );

    if (currentPageNumber >= pageCount) {
      setCurrentPageNumber(0);
    }
  }, [errorListData, pageSize]);

  const rows =
    transformApiDataToRows(errorListData?.errorList, gridType, pageSize) || [];

  const showExport = gridType === ErrorGridType.ProductionErrors;

  const exportMutation = useExportErrorList(
    (_data) => {
      setToastMessage(
        t(
          'productionsAndExports.createNewProduction.generate.modals.errors.toastMessage'
        )
      );
      setToastStatus([ToastType.Success]);
      setShowToast(true);
    },
    (error) => {
      setToastMessage(error.message || '');
      setToastStatus([ToastType.Error]);
      setShowToast(true);
    }
  );

  const handleExportErrors = () => {
    exportMutation.mutate({
      productionId,
      SecurityGroupID: securityGroupId,
      ModuleType: ModuleType.EXPORT,
    });
  };

  return (
    <ArrModal
      showModal={open}
      onClose={onClose}
      className={`modal ${
        isReplaceSeedPdfFlyoutOpen ? 'modal--flyout-open' : ''
      }`}
      dataTestId="error-grid-modal"
      headerTitleElement={
        <Typography
          component="div"
          className="title-text typo-text headline3_size"
          data-testid="title-text"
        >
          <span data-testid={`error-grid-modal-span-text`}>{title}</span>
          <span
            className="info-icon-wrapper"
            data-testid={`error-grid-modal-span-btn`}
          >
            <ArrTooltipWrapper
              trigger={<InfoIcon fontSize="medium" className="info-icon" />}
            >
              {tooltip}
            </ArrTooltipWrapper>
          </span>
        </Typography>
      }
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="grid-container"
        data-testid="grid-container"
      >
        <DataGridPro
          data-testid="error-data-grid"
          className="data-grid-container"
          rows={rows}
          columns={columns}
          pagination
          paginationMode="server"
          sortingMode="server"
          disableColumnMenu={true}
          disableColumnReorder={true}
          getRowHeight={() => 'auto'}
          slots={{footer: FooterWrapper}}
          slotProps={{
            footer: {
              pageSizeOptions: [10, 25, 50],
              showExport,
              pageSize: pageSize,
              pageCount: pageCount,
              currentPageNumber: currentPageNumber,
              onClose,
              onExport: handleExportErrors,
              onPageSizeChange: setPageSize,
              onPageNumberChange: setCurrentPageNumber,
              onPageCountChange: setPageCount,
            } as any,
          }}
          onSortModelChange={setSortModel}
          sortModel={sortModel}
        />
      </div>
      {showToast && (
        <ArrToastNotification
          data-testid="toast-notification"
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
          setErrorIconAsAlert={toastStatus[0] !== ToastType.Success}
        />
      )}
      <ReplaceSeedPdfFlyout
        isOpen={isReplaceSeedPdfFlyoutOpen}
        documentId={selectedDocumentId}
        productionId={productionId}
        onClose={handleReplaceSeedPdfFlyoutClose}
        onSuccess={() => {
          // Optionally refresh the error list or show success message
        }}
      />
    </ArrModal>
  );
};

export default ErrorGridModal;
